// ########################## Reusable Table + Doughnut Chart #####################################
/* eslint-disable */
import React, { useMemo, useState, useEffect } from "react";
import styles from "./miniTableComp.module.css";

import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    Title,
} from "chart.js";

import { Doughnut } from "react-chartjs-2";

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    Title
);

const GLAgingDoughnutChartTable = ({ title, columns, data, DataTitle = "" }) => {

    const [showPopup, setShowPopup] = useState(false);
    const [viewMode, setViewMode] = useState("chart");

    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc",
    });

    const [sortedData, setSortedData] = useState([]);

    useEffect(() => {
        setSortedData([...data]);
    }, [data]);

    useEffect(() => {
        if (sortConfig.key) {
            const newSortedData = [...data].sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];
                if (typeof aValue === "number" && typeof bValue === "number") {
                    return sortConfig.direction === "asc"
                        ? aValue - bValue
                        : bValue - aValue;
                }
                const aString = String(aValue || "");
                const bString = String(bValue || "");
                return sortConfig.direction === "asc"
                    ? aString.localeCompare(bString)
                    : bString.localeCompare(aString);
            });

            setSortedData(newSortedData);
        } else {
            setSortedData([...data]);
        }
    }, [sortConfig, data]);

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            if (sortConfig.direction === "asc") {
                setSortConfig({ key, direction: "desc" });
            } else {
                setSortConfig({ key: null, direction: "asc" });
            }
        } else {
            setSortConfig({ key, direction: "asc" });
        }
    };

    const columnTotals = useMemo(() => {
        const totals = {};
        const dataToUse = sortConfig.key ? sortedData : data;
        columns.forEach((col) => {
            totals[col.key] = 0;
        });
        dataToUse.forEach((row) => {
            columns.forEach((col) => {
                const value = row[col.key];

                if (typeof value === "number" && !isNaN(value)) {
                    totals[col.key] += value;
                }
            });
        });
        Object.keys(totals).forEach((key) => {
            totals[key] = parseFloat(totals[key].toFixed(2));
        });
        return totals;
    }, [columns, data, sortedData, sortConfig]);

    const chartData = useMemo(() => {
        if (!data?.length) return null;
        const agingMap = {
            "0_30": "0-1 Month",
            "30_60": "1-2 Months",
            "60_90": "2-3 Months",
            "90_180": "3-6 Months",
            "181_365": "6-12 Months",
            "1_3_year": "1-3 Years",
            "3_6_year": ">3 Years",
        };
        const totals = {};

        Object.keys(agingMap).forEach(key => {
            totals[key] = 0;
        });

        data.forEach(row => {
            Object.keys(agingMap).forEach(key => {
                const countKey = `${key}_count`;
                const value =
                    Number(row[countKey]) ||
                    Number(row[key]) ||
                    0;

                totals[key] += value;
            });
        });

        const rawValues = Object.keys(agingMap).map(
            key => totals[key]
        );
        const grandTotal = rawValues.reduce((a, b) => a + b, 0);

        const percentages = rawValues.map(val =>
            grandTotal ? Number(((val / grandTotal) * 100).toFixed(1)) : 0
        );

        const rawValuesCr = Object.keys(agingMap).map(
            key => totals[key]
        );

        // const labels = Object.values(agingMap);
        const labels = Object.keys(agingMap).map((key, index) => {
            const label = agingMap[key];
            const valueCr = rawValuesCr[index] || 0;

            return `${label} (${valueCr.toFixed(2)})`;
        });
        const colors = [
            "#16A34A", 
            "#4ADE80", 
            "#A3E635", 
            "#FACC15",
            "#FB923C",
            "#F97316",
            "#DC2626"
        ];

        const totalCr = rawValuesCr.reduce((a, b) => a + b, 0);
        labels.push(`Total (${totalCr.toFixed(2)})`);
        colors.push("#374151");

        return {
            labels,
            datasets: [
                {
                    label: "Aging %",
                    data: percentages,
                    backgroundColor: colors,
                    borderWidth: 1,
                },
            ],
        };
    }, [data]);



    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "52%",
        plugins: {
            // legend: {
            //     position: "right",
            //     labels: {
            //         usePointStyle: true,
            //         pointStyle: "circle",
            //         boxWidth: 12,
            //         boxHeight: 12,
            //         padding: 12,
            //         font: { size: 11 },
            //     },
            // },
            // legend: {
            //     position: "right",
            //     labels: {
            //         usePointStyle: true,
            //         pointStyle: "circle",
            //         boxWidth: 12,
            //         boxHeight: 12,
            //         padding: 10,
            //         font: { size: 11 },
            //         generateLabels: (chart) => {
            //         const data = chart.data;
            //         const bgColors = data.datasets[0].backgroundColor;
            //         return data.labels.map((label, i) => {
            //             const isTotal = label.startsWith("Total");
            //             return {
            //             text: label,
            //             fillStyle: isTotal
            //                 ? "#6366F1"
            //                 : bgColors[i],

            //             strokeStyle: isTotal
            //                 ? "#6366F1"
            //                 : bgColors[i],

            //             hidden: false,
            //             index: i,
            //             };
            //         });
            //         },
            //     },
            // },
            legend: {
                position: "right",
                onClick: (e, legendItem, legend) => {
                    const index = legendItem.index;
                    const chart = legend.chart;
                    chart.toggleDataVisibility(index);
                    chart.update();
                },

                labels: {
                    usePointStyle: true,
                    pointStyle: "circle",
                    boxWidth: 12,
                    boxHeight: 12,
                    padding: 10,
                    font: { size: 11 },

                    generateLabels: (chart) => {
                        const data = chart.data;
                        const dataset = data.datasets[0];
                        const bgColors = dataset.backgroundColor;

                        return data.labels.map((label, i) => {
                            const isHidden = !chart.getDataVisibility(i);
                            const isTotal = label.startsWith("Total");

                            return {
                                text: label,

                                fillStyle: isHidden
                                    ? "rgba(0,0,0,0.25)"
                                    : isTotal
                                        ? "#6366F1"
                                        : bgColors[i],

                                strokeStyle: isTotal
                                    ? "#6366F1"
                                    : bgColors[i],

                                hidden: isHidden,
                                index: i,
                            };
                        });
                    },
                },
            },
            datalabels: {
                color: "#1F2933",
                anchor: "center",
                align: "center",
                offset: 10,
                clamp: true,
                font: {
                    weight: "bold",
                    size: 10,
                },
                formatter: (value) => `${value} %`,
            },
            title: {
                display: false,
                text: title,
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${ctx.raw}`,
                },
            },
        },
    };

    const downloadTableAsCSV = () => {
        const dataToDownload = sortConfig.key ? sortedData : data;
        const headers = columns.map(col => `"${col.header}"`).join(",");
        const rows = dataToDownload.map(row => {
            return columns
                .map(col => `"${row[col.key]}"`)
                .join(",");
        });

        const totalRow = columns
            .map((col, index) => {
                if (index === 0) return '"Total"';
                if (
                    typeof columnTotals[col.key] === "number" &&
                    columnTotals[col.key] !== 0
                ) {
                    return `"${columnTotals[col.key]}"`;
                }
                return '""';
            })
            .join(",");

        const csvContent = [headers, ...rows, totalRow].join("\n");
        const blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
            "download",
            `${title.replace(/\s+/g, "_")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const hasYellowBackground = (headerText) => {
        if (!headerText) return false;
        const text = String(headerText).toLowerCase();
        return text.includes('1-3') && (text.includes('yrs') || text.includes('years'));
    };

    const hasOrangeBackground = (headerText) => {
        if (!headerText) return false;
        const text = String(headerText).toLowerCase();
        return text.includes('>3') && (text.includes('yrs') || text.includes('years'));
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
        return <span className={styles.sort_icon}>▲</span>;
        }
        
        if (sortConfig.direction === 'asc') {
        return <span className={styles.sort_icon_active}>▲</span>;
        }
        
        return <span className={styles.sort_icon_active}>▼</span>;
    };

    const renderTable = () => (
        <div className={styles.table_scroll}>
            <table className={styles.custom_table}>
                <thead>
                    <tr>
                        {columns.map((col, index) => (
                            <th
                                key={index}
                                onClick={() => col.sortable && handleSort(col.key)}
                                className={`${col.sortable ? styles.sortable_header : ""} ${
                                    hasYellowBackground(col.header) ? styles.yellow_header :
                                    hasOrangeBackground(col.header) ? styles.orange_header : ""
                                }`}
                            >
                                {col.header}
                                {col.sortable && renderSortIcon(col.key)}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(sortConfig.key ? sortedData : data).map((row, i) => (
                        <tr key={i}>
                            {columns.map((col, j) => (
                                <td key={j}
                                    className={`${col.sortable ? styles.sortable_header : ""} ${
                                        hasYellowBackground(col.header) ? styles.yellow_header :
                                        hasOrangeBackground(col.header) ? styles.orange_header : ""
                                    }`}
                                >
                                    {row[col.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className={styles.total_row}>
                        {columns.map((col, i) => (
                            <td key={i}
                                className={`${col.sortable ? styles.sortable_header : ""} ${
                                    hasYellowBackground(col.header) ? styles.yellow_header :
                                    hasOrangeBackground(col.header) ? styles.orange_header : ""
                                }`}
                            >
                                {typeof columnTotals[col.key] === "number"
                                    ? columnTotals[col.key]
                                    : i === 0
                                        ? "Total"
                                        : ""}
                            </td>
                        ))}
                    </tr>
                </tfoot>

            </table>
        </div>
    );

    const renderChart = () => (
        <div style={{ height: "350px", width: "100%" }}>
            {chartData && (
                <Doughnut data={chartData} options={chartOptions} height={160} />
            )}
        </div>
    );

    return (
        <>
            <div className={styles.table_container}>
                <div className={styles.table_header_container}>
                    <div className={styles.button_group}>
                        <button
                            className={styles.popup_button}
                            onClick={() => setShowPopup(true)}
                        >
                            &#8505;
                        </button>
                        <button
                            className={styles.download_button}
                            onClick={downloadTableAsCSV}
                        >
                            &#11123;
                        </button>
                    </div>
                    <div
                        className={styles.table_header_title}
                        title={DataTitle}
                    >
                        {title}
                    </div>
                    <div className={styles.toggle_container}>
                        <span className={styles.toggle_label}>Table</span>
                        <label className={styles.switch}>
                            <input
                                type="checkbox"
                                checked={viewMode === "chart"}
                                onChange={() =>
                                    setViewMode(viewMode === "table" ? "chart" : "table")
                                }
                            />
                            <span className={`${styles.slider} ${styles.round}`}></span>
                        </label>
                        <span className={styles.toggle_label}>Chart</span>
                    </div>
                </div>
                {viewMode === "table" ? renderTable() : renderChart()}
            </div>
            {showPopup && (
                <div className={styles.popup_overlay}>
                    <div className={styles.popup_container}>
                        <div className={styles.popup_header}>
                            <h3>{title}</h3>
                            <div
                                className={styles.toggle_container}
                                style={{ marginRight: "12px" }}
                            >
                                <span className={styles.toggle_label}>Table</span>
                                <label className={styles.switch}>
                                    <input
                                        type="checkbox"
                                        checked={viewMode === "chart"}
                                        onChange={() =>
                                            setViewMode(viewMode === "table" ? "chart" : "table")
                                        }
                                    />
                                    <span className={`${styles.slider} ${styles.round}`}></span>
                                </label>
                                <span className={styles.toggle_label}>Chart</span>
                            </div>
                            <button
                                className={styles.download_button_popup}
                                onClick={downloadTableAsCSV}
                            >
                                &#11123;
                            </button>
                            <button
                                className={styles.close_button}
                                onClick={() => setShowPopup(false)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className={styles.popup_content}>
                            {viewMode === "table"
                                ? renderTable()
                                : renderChart()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GLAgingDoughnutChartTable;
